/**
 * Social Flex Microcopy Bank — Phase 1 + 5
 * ----------------------------------------
 * Lightweight, deterministic copy provider for competitive / FOMO / creator
 * virality messaging. Pure functions. No I/O. No AI. No polling.
 *
 * The same kind taxonomy as offsetOrchestration so a public surface that
 * already knows the dominant signal can ask for a flex line in one call.
 */
import type { DominantKind } from "@/lib/viralIntel/emotionalIntensityCoordinator";

export type FlexLang = "ES" | "EN" | "PT";

/** Generic competitive lines — used as fallback / rotation across screens. */
const COMPETITIVE_BANK: Record<FlexLang, string[]> = {
  ES: [
    "VerseSport detectó presión extrema.",
    "El crowd reaccionó antes.",
    "Momentum shift detected.",
    "Heat Window activa.",
    "VerseSport alertó primero.",
    "Confidence surge.",
    "Pánico de nación en aumento.",
    "La comunidad ya siente el cambio.",
    "VerseSport users reaccionan antes.",
    "El crowd detectó peligro.",
  ],
  EN: [
    "VerseSport detected extreme pressure.",
    "The crowd reacted first.",
    "Momentum shift detected.",
    "Heat Window active.",
    "VerseSport alerted first.",
    "Confidence surge.",
    "Nation panic rising.",
    "The community is already feeling it.",
    "VerseSport users react faster.",
    "The crowd detected danger.",
  ],
  PT: [
    "VerseSport detectou pressão extrema.",
    "A torcida reagiu antes.",
    "Mudança de momentum detectada.",
    "Heat Window ativa.",
    "VerseSport alertou primeiro.",
    "Confidence surge.",
    "Pânico nacional em alta.",
    "A comunidade já sente a mudança.",
    "Usuários VerseSport reagem antes.",
    "A torcida detectou perigo.",
  ],
};

/** Kind-specific flex taglines paired with the dominant emotional signal. */
const KIND_BANK: Record<Exclude<DominantKind, "idle">, Record<FlexLang, string[]>> = {
  heat_window: {
    ES: ["Heat Window crítica.", "Zona caliente detectada antes."],
    EN: ["Critical Heat Window.", "Hot zone detected early."],
    PT: ["Heat Window crítica.", "Zona quente detectada antes."],
  },
  story_arc: {
    ES: ["Arco narrativo escalando.", "La historia está cambiando."],
    EN: ["Story arc escalating.", "The story is turning."],
    PT: ["Arco narrativo escalando.", "A história está mudando."],
  },
  contrarian: {
    ES: ["Lectura contraria detectada.", "El mercado va contra el crowd."],
    EN: ["Contrarian read detected.", "Market vs. crowd divergence."],
    PT: ["Leitura contrária detectada.", "Mercado contra a torcida."],
  },
  viral_card: {
    ES: ["Momento viral cargando.", "Información privilegiada."],
    EN: ["Viral moment loading.", "Insider-grade signal."],
    PT: ["Momento viral carregando.", "Sinal de bastidor."],
  },
  crowd: {
    ES: ["El crowd se mueve primero.", "Reacción colectiva detectada."],
    EN: ["Crowd moving first.", "Collective reaction detected."],
    PT: ["A torcida se move primeiro.", "Reação coletiva detectada."],
  },
};

/** Creator virality hooks — designed to look like privileged info in caps. */
const CREATOR_HOOK_BANK: Record<FlexLang, string[]> = {
  ES: [
    "INFORMACIÓN PRIVILEGIADA",
    "DETECTADO ANTES QUE NADIE",
    "VERSESPORT LO VIO PRIMERO",
    "SEÑAL TEMPRANA",
    "VENTAJA COMPETITIVA",
  ],
  EN: [
    "INSIDER SIGNAL",
    "SEEN BEFORE EVERYONE",
    "VERSESPORT SAW IT FIRST",
    "EARLY SIGNAL",
    "COMPETITIVE EDGE",
  ],
  PT: [
    "SINAL DE BASTIDOR",
    "DETECTADO ANTES DE TODOS",
    "VERSESPORT VIU PRIMEIRO",
    "SINAL ANTECIPADO",
    "VANTAGEM COMPETITIVA",
  ],
};

function pick<T>(arr: T[], seed: number): T {
  if (arr.length === 0) return arr[0];
  const idx = Math.abs(Math.floor(seed)) % arr.length;
  return arr[idx];
}

/**
 * Pick a competitive flex line. `seed` should be a stable per-moment number
 * (e.g. minute*100 + scoreHome*7 + scoreAway*3) so the same moment shows
 * the same line and renders are not jittery across re-renders.
 */
export function pickFlexLine(
  kind: DominantKind,
  seed: number,
  lang: FlexLang = "ES",
): string {
  if (kind === "idle") return pick(COMPETITIVE_BANK[lang], seed);
  const bank = KIND_BANK[kind][lang];
  return pick(bank, seed);
}

export function pickCreatorHook(seed: number, lang: FlexLang = "ES"): string {
  return pick(CREATOR_HOOK_BANK[lang], seed);
}

export function pickCompetitiveLine(seed: number, lang: FlexLang = "ES"): string {
  return pick(COMPETITIVE_BANK[lang], seed);
}
